/**
 * `contra` 结算与明细契约 —— **Q 域**（`DR-047` · 批 82）
 *
 * 依据：`Q域PRD` **`AC-Q2.4`**（部分还款后余额 = 剩余）· **`AC-Q2.5`**（减免/核销不计收支）·
 * **`AC-Q2.6`**（多还允许、不截断、给提示）· **`AC-Q5.1`**（三类行）· **`AC-Q5.2`**（不下发已还期次）·
 * `AC-Q4.3`（L3 明细）· `DR-022` §2.2/§2.3 · `DR-044` D6（`type` 取值）·
 * 架构 §6.1（契约唯一源）· §4.2（`contra_settlements` 字段）
 *
 * ## 🔴 三条"契约层就该堵死"的东西
 *
 * 1. **金额 > 0 的整数分** —— 0 元结算无意义；负数方向由**动作**表达（`collect`/`repay`），
 *    不由符号表达（否则 `-100` 的 `repay` 与 `+100` 的 `collect` 会变成两个等价入口）。
 * 2. **`waive` 与对手方账户互斥** —— 核销是单腿动作（`DR-047` D3）：
 *    `waive` ⇒ `counterAccountId` 必须为空；其余 ⇒ 必填。**在契约层判**（它能判），
 *    但"账户是否存在 / 币种是否一致 / 是不是贷款账户"留给服务层（它才知道 404 / 400 的区别）。
 * 3. 🔴 **`direction` 由服务端算好下发** —— 端侧不判"这笔是收还是付"（`AC-Q4.6` 同一纪律：
 *    色彩与方向都不靠端侧猜）。
 */

import { z } from 'zod';
import { SettlementType } from '../enums/account.js';
import type { Cents } from '../types/brand.js';
import { DayString } from './pagination.js';

/** 结算动作取值（3 类，`DR-044` D6 · `DR-047` D1） */
export const SettlementTypeSchema = z.enum([
  SettlementType.Collect,
  SettlementType.Repay,
  SettlementType.Waive,
]);

/**
 * `POST /api/contra/:accountId/settle` —— 记一次结算（收回 / 还款 / 核销）。
 *
 * 🔴 **贷款账户不走本接口**（`DR-047` D5）：贷款还款要同时翻转期次状态、可能带利息腿、
 * 可能重排 —— 那是 `LoanScheduleService` 的地盘。对 `loan` 调用会得到 **400 + 指路文案**。
 */
export const SettleContraRequestSchema = z
  .object({
    type: SettlementTypeSchema,
    /** 整数分，> 0（`AC-Q2.6`：允许超过未结余额，**由服务层判**并回 `overSettled`） */
    amount: z.number().int('金额必须是整数分').positive('结算金额必须大于 0'),
    /** 账本本地日历日 */
    date: DayString,
    /**
     * 收 / 付款账户；`waive` 时必须为空（见下 `superRefine`）。
     *
     * 🔴 **刻意不写 `.min(1)`**：空串在这里的语义就是"没给"（表单里"未选"就是 `''`），
     *    让 `superRefine` 用同一个 `has` 判据统一处理 —— 否则空串会先被 `.min(1)` 拦成
     *    "格式错误"，而用户只是没选（误导性文案）。
     */
    counterAccountId: z.string().trim().nullish(),
    note: z.string().trim().max(200).nullish(),
  })
  .superRefine((v, ctx) => {
    const needs = v.type !== SettlementType.Waive;
    const has = v.counterAccountId !== null && v.counterAccountId !== undefined && v.counterAccountId !== '';
    if (needs && !has) {
      ctx.addIssue({
        code: 'custom',
        path: ['counterAccountId'],
        message: '收回 / 还款必须指定收付款账户',
      });
    }
    if (!needs && has) {
      ctx.addIssue({
        code: 'custom',
        path: ['counterAccountId'],
        message: '核销不产生资金流动，不需要收付款账户',
      });
    }
  });
export type SettleContraRequest = z.infer<typeof SettleContraRequestSchema>;

// ═══════════════════════════════ 视图 ═══════════════════════════════

/** 一行结算（`contra_settlements` 的对外形状 —— `DR-047` D2：**不含 `transactionId`**） */
export interface SettlementView {
  id: string;
  accountId: string;
  type: SettlementType;
  /** 动作中文名（服务端下发：`收回` / `还款` / `核销`） */
  typeLabel: string;
  /** 整数分，恒为正 */
  amount: Cents;
  /** 账本本地日历日 */
  date: string;
  note: string | null;
  createdAt: number;
}

/**
 * 结算结果。
 *
 * 🔴 `overSettled` 是**旗标而不是错误**（`AC-Q2.6` 原文：「允许（余额反向）、**不截断**、给出提示」）——
 * 端侧据此显示提示条，**不得**把它当失败处理。
 */
export interface SettleContraResultView {
  settlement: SettlementView;
  /** 结算后的账户余额（整数分，**符号保留** —— 可能反向） */
  balanceAfterCents: Cents;
  /** 本次结算额是否**超过**结算前未结余额 */
  overSettled: boolean;
  /** 本次产生的流水 id（供端侧"看一眼"；**不落进结算行** —— `DR-047` D2） */
  transactionId: string;
}

/**
 * L3 明细的一行（`AC-Q5.1` 三类行）。
 *
 * | `kind` | 含义 | 来源 |
 * |---|---|---|
 * | `txn` | 交易行（该账户上的腿；同一笔流水的多条腿**合并成 1 行**） | `transactions` + `legs` |
 * | `settlement` | 结算行（**本金的唯一痕迹**） | `contra_settlements` |
 * | `pendingPeriod` | 待还期次行（🔴 **只下发 `pending`** —— `AC-Q5.2` 修正 v1 病灶） | `loan_schedules` |
 */
export interface ContraTimelineRow {
  kind: 'txn' | 'settlement' | 'pendingPeriod';
  /** 账本本地日历日（排序键，时间倒序） */
  date: string;
  /** 整数分，恒为正 */
  amount: Cents;
  /**
   * 🔴 **对本金的方向**（服务端算好，端侧不判）：
   * - `toContra` = 让 `contra` 余额**增加**（如借出、还款到应收账户）；
   * - `fromContra` = 让 `contra` 余额**减少**（如收回、核销）。
   */
  direction: 'toContra' | 'fromContra';
  /** 展示标题（服务端下发；端侧不维护映射表） */
  label: string;
  /** 备注（有则显示） */
  note: string | null;
  /** `kind='txn'` 时的流水 id */
  transactionId: string | null;
  /** `kind='settlement'` 时的结算行 id */
  settlementId: string | null;
  /** `kind='pendingPeriod'` 时的期次序号（从 0 起） */
  periodIndex: number | null;
}

/**
 * `GET /api/contra/:accountId/timeline` —— L3 明细（`AC-Q4.3` / `AC-Q5.1`）。
 *
 * 🔴 不分页：单个借贷账户的明细是**有界小集合**（迁移账户最多 267 期 + 数十笔），
 * 且端侧明细抽屉要能一次看全（与账户列表同判断，与流水列表相反）。
 */
export interface ContraTimelineView {
  accountId: string;
  currency: string;
  /** 账户名（含事项）—— 抽屉标题用 */
  accountName: string;
  /** 对手方（`DR-046`）；迁移行上等于 `accountName` */
  counterparty: string | null;
  /** 🔴 只含 `pending` 的期次（`AC-Q5.2`）—— 已还 / 已作废**都不出现** */
  rows: readonly ContraTimelineRow[];
  /** 当前余额（整数分，符号保留） */
  balanceCents: Cents;
  /**
   * 🆕 `AC-Q5.3` 本金不变量（`DR-048` D8）。
   *
   * 🔴 `null` = **诚实留白**（该账户没有"本金总额"这个分母）——
   * 非 `loan` 的 `contra`（应收 / 应付 / 垫付 / 报销）原始总额在 v2 无载体，
   * 用"当前余额 + 已结算"倒推分母就是编数。原因见 {@link invariantNotCheckedReason}。
   */
  invariant: ContraPrincipalInvariantView | null;
  /** `invariant === null` 时的原因（中文，端侧直接展示；`invariant` 非空时为 `null`） */
  invariantNotCheckedReason: string | null;
}

/**
 * `AC-Q5.3` 的对外形状 —— 四个量**来自四个不同的取数口**（见内核
 * `checkContraPrincipalInvariant()` 的说明）。
 *
 * 🔴 **批 84 订正（`DR-049` D9）**：判定量从「结算行 + pending vs 期次合计」改成
 * 「**结算行 vs 本金腿**」—— 前者在**提前还款重排**后必然失败（重排把期次合计改小、结算行变大）。
 * `pendingPrincipalCents` / `totalPrincipalCents` 保留但**仅供展示**，不参与判定。
 */
export interface ContraPrincipalInvariantView {
  /** 登记侧：结算行合计（`DR-022` §2.3「结算行 = 本金的唯一痕迹」） */
  cashPrincipalCents: Cents;
  /** 现金侧：该账户上「无分类 + 不计收支」的腿合计（`pay` / `prepay` / `settle` 三条路径同形） */
  transactionPrincipalCents: Cents;
  /** 待还期次本金（**仅展示**，不参与判定） */
  pendingPrincipalCents: Cents;
  /** 全部期次本金合计（**仅展示**，不参与判定 —— 它会随重排漂移） */
  totalPrincipalCents: Cents;
  /** `结算行 − 本金腿`（正 = 结算行多写；负 = 漏写） */
  diffCents: number;
  balanced: boolean;
}

// ═══════════════════════════════ 报销分摊（`AC-Q3.x` · `DR-048`） ═══════════════════════════════

/** 一条微调（`AC-Q3.6`）—— 键 = 分类 × 年度，与内核 `ReimburseAllocationInput` 同形 */
export const ReimburseAllocationSchema = z.object({
  /** 原支出分类；`null` = 未分类池 */
  categoryId: z.string().trim().nullable(),
  /** 垫付年度（池的第二个键） */
  year: z.number().int('年度必须是整数').min(2000, '年度不合理').max(2100, '年度不合理'),
  /** 该池本次应分摊的金额（整数分，≥ 0；上限由**服务端**按该池垫付额判） */
  amountCents: z.number().int('金额必须是整数分').min(0, '分摊额不能为负'),
});
export type ReimburseAllocation = z.infer<typeof ReimburseAllocationSchema>;

/**
 * `POST /api/contra/:accountId/reimburse/preview` —— **只读预演**（`AC-Q3.6`）。
 *
 * 🔴 只读是硬要求：预演**不写任何表**（含 `contra_settlements`）。
 * 与批 82 的 `settle` 分成两个路由、而不是"带 `dryRun` 旗标"，
 * 是因为**写权限**与**读权限**将来可能要分开（预演对只读成员也应可用）。
 */
export const ReimbursePreviewRequestSchema = z.object({
  /** 报销到账额（整数分、> 0） */
  amount: z.number().int('金额必须是整数分').positive('报销额必须大于 0'),
  /** 报销到账日（账本本地日历日） */
  date: DayString,
  /**
   * 可选微调 —— 预演**也**接受它。
   *
   * 🔴 理由：`AC-Q3.6` 是「**预演 → 可微调 → 确认**」，用户改完必须先看到"改后的结果"
   * 才能确认。若微调只在落账接口可用，端侧就只剩两个选择：自己算（违反 `AC-Q6.4`）
   * 或者"让用户盲提交"（服务端 400 了才知道错）。两者都不行 ⇒ 预演必须支持微调。
   */
  allocations: z.array(ReimburseAllocationSchema).optional(),
});
export type ReimbursePreviewRequest = z.infer<typeof ReimbursePreviewRequestSchema>;

/**
 * `POST /api/contra/:accountId/reimburse` —— 落账。
 *
 * `allocations` 缺省 = **零额外输入**（按垫付比例自动分摊）；
 * 给了就必须满足内核的三条校验（池存在 / ≤ 该池垫付额 / 合计 === 可分摊额），
 * 否则 **400**（服务端**不自动修正** —— 自动修会让用户以为"我改的数字生效了"）。
 */
export const ReimburseConfirmRequestSchema = ReimbursePreviewRequestSchema.extend({
  /** 收款账户（银行 / 现金）；必填（报销要落地到某个真实账户） */
  counterAccountId: z.string().trim().min(1, '请选择收款账户'),
  note: z.string().trim().max(200).nullish(),
});
export type ReimburseConfirmRequest = z.infer<typeof ReimburseConfirmRequestSchema>;

/** 一个分摊池的对外形状（= 内核 `ReimbursePoolView`，契约层逐字段镜像） */
export interface ReimbursePoolResultView {
  /** 原支出分类；`null` = 未分类池（落账时冲减腿挂兜底 L2 `cat-other-offset`） */
  categoryId: string | null;
  year: number;
  advanceCents: Cents;
  allocatedCents: Cents;
  reductionCents: Cents;
  crossYearIncomeCents: Cents;
}

/**
 * 预演结果 —— 端侧**只渲染、不自算**（`AC-Q6.4` / `DR-023` 反指标「端侧金额聚合 = 0」）。
 *
 * 🔴 `balanced` 为 `false` 或 `issues` 非空时：预演**照实下发**（让用户看到"这样填会错在哪"），
 * 但落账接口会 **400**。
 */
export interface ReimbursePreviewView {
  accountId: string;
  accountName: string;
  currency: string;
  /** 报销到账额 */
  reimburseCents: Cents;
  /** 该账户垫付合计（全部年度） */
  advanceTotalCents: Cents;
  /** 可分摊总额 = `min(报销额, 垫付合计)` */
  allocatableCents: Cents;
  reductionCents: Cents;
  crossYearIncomeCents: Cents;
  excessCents: Cents;
  /** 转收入合计 = 跨年 + 超额（落账时合成 1 条收入腿） */
  incomeCents: Cents;
  pools: readonly ReimbursePoolResultView[];
  balanced: boolean;
  issues: readonly string[];
}

/** 落账结果 */
export interface ReimburseResultView {
  /** 本次产生的流水 id */
  transactionId: string;
  /** 实际落账的分摊（= 落账前最后一次预演，供端侧"我确认的就是这个"） */
  preview: ReimbursePreviewView;
  /** 落账后的 `contra` 余额（整数分，符号保留） */
  balanceAfterCents: Cents;
}
